import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AdminService } from '../../services/AdminService/admin';
import { CompanyService } from '../../services/CompanyService/company';
import { AuthService } from '../../services/AuthService/auth';
import { CountryEventTemplateResponse } from '../../models/country-config.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { findCountry } from '../../shared/countries';
import {
  UpsertCountryEventTemplateDialog,
  UpsertCountryEventTemplateDialogData,
} from '../../dialogs/upsert-country-event-template-dialog/upsert-country-event-template-dialog';

@Component({
  selector: 'app-country-xml-templates',
  imports: [CommonModule, MatButtonModule, MatIconModule, ErrorBanner, EmptyState],
  templateUrl: './country-xml-templates.html',
  styleUrl: './country-xml-templates.scss',
})
export class CountryXmlTemplates implements OnInit {
  // Which countries have a configured schema at all — same source the field-mapping page uses,
  // so a country shows up here even before any XML template has been added for it.
  availableCountries = signal<string[]>([]);
  selectedCountry = signal('');

  isLoading = signal(false);
  errorMessage = signal('');

  templates = signal<CountryEventTemplateResponse[]>([]);
  isLoadingTemplates = signal(false);
  templatesError = signal('');
  // Keyed by country — `templates` mirrors whichever entry matches selectedCountry() so that
  // switching back to an already-fetched country restores its own data instead of leaving
  // whatever country was fetched most recently sitting in a single shared signal.
  private templatesByCountry = new Map<string, CountryEventTemplateResponse[]>();

  constructor(
    private admin: AdminService,
    private companyService: CompanyService,
    private auth: AuthService,
    private dialog: MatDialog
  ) {}

  get canEditTemplates(): boolean {
    return this.auth.getSession()?.role === 'SUPERADMIN';
  }

  ngOnInit(): void {
    // Company roles are always tied to a companyId (see role-families.ts); platform roles never
    // are. Preselect their own country so exporters land on the schema they most likely need.
    if (!this.auth.getSession()?.companyId) {
      this.load();
      return;
    }

    this.companyService.me().subscribe({
      next: (res) => {
        this.selectedCountry.set(res.data.countryCode);
        this.load();
      },
      error: () => this.load(),
    });
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.admin.listTermRules().subscribe({
      next: (res) => {
        this.isLoading.set(false);
        const countries = Array.from(new Set(res.data.map((r) => r.countryCode))).sort();
        this.availableCountries.set(countries);
        if ((!this.selectedCountry() || !countries.includes(this.selectedCountry())) && countries.length > 0) {
          this.selectedCountry.set(countries[0]);
        }
        if (this.selectedCountry()) {
          this.loadTemplates();
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load country schemas.'));
      },
    });
  }

  countryLabel(code: string): string {
    const country = findCountry(code);
    return country ? `${code} · ${country.name}` : code;
  }

  selectCountry(code: string): void {
    this.selectedCountry.set(code);
    this.loadTemplates();
  }

  private loadTemplates(force = false): void {
    const country = this.selectedCountry();
    if (!country) {
      return;
    }
    if (!force && this.templatesByCountry.has(country)) {
      this.templates.set(this.templatesByCountry.get(country)!);
      return;
    }

    this.isLoadingTemplates.set(true);
    this.templatesError.set('');

    this.admin.listEventTemplates(country).subscribe({
      next: (res) => {
        this.isLoadingTemplates.set(false);
        this.templatesByCountry.set(country, res.data);
        // The fetch is async — only apply it if this is still the selected country, otherwise a
        // slow response for a country the user has already switched away from would clobber it.
        if (this.selectedCountry() === country) {
          this.templates.set(res.data);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingTemplates.set(false);
        this.templatesError.set(extractErrorMessage(err, 'Failed to load XML templates.'));
      },
    });
  }

  get documentTemplate(): CountryEventTemplateResponse | undefined {
    return this.templates().find((t) => t.scope === 'DOCUMENT');
  }

  get eventTemplates(): CountryEventTemplateResponse[] {
    return this.templates()
      .filter((t) => t.scope === 'EVENT')
      .sort((a, b) => {
        if (a.eventType === '*') return -1;
        if (b.eventType === '*') return 1;
        return a.eventType.localeCompare(b.eventType);
      });
  }

  eventTemplateLabel(eventType: string): string {
    return eventType === '*' ? 'Every other type (wildcard)' : eventType;
  }

  openTemplateDialog(existing?: CountryEventTemplateResponse, defaultScope?: 'DOCUMENT' | 'EVENT'): void {
    const data: UpsertCountryEventTemplateDialogData = { countryCode: this.selectedCountry(), existing, defaultScope };
    this.dialog
      .open(UpsertCountryEventTemplateDialog, { width: '640px', data })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.loadTemplates(true);
        }
      });
  }

  deleteTemplate(template: CountryEventTemplateResponse): void {
    const label = template.scope === 'DOCUMENT' ? 'the document envelope' : `the "${template.eventType}" fragment`;
    if (!confirm(`Remove ${label} for ${this.selectedCountry()}? Previews for it will fail until a new one is set.`)) {
      return;
    }

    this.templatesError.set('');
    this.admin
      .deleteEventTemplate(
        this.selectedCountry(),
        template.scope,
        template.eventType !== '*' ? template.eventType : undefined
      )
      .subscribe({
        next: () => this.loadTemplates(true),
        error: (err: HttpErrorResponse) => {
          this.templatesError.set(extractErrorMessage(err, 'Failed to remove the template.'));
        },
      });
  }
}
